import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logActivityDirect } from '@/hooks/useActivityLogger';

type AppRole = 'admin' | 'user';
type PermissionType = 
  | 'access_editor'
  | 'view_templates'
  | 'edit_templates'
  | 'delete_templates'
  | 'manage_own_templates'
  | 'use_kiosk'
  | 'view_data_panel'
  | 'use_json_editor'
  | 'batch_import'
  | 'print_labels'
  | 'export_pdf'
  | 'use_data_carousel'
  | 'change_label_size'
  | 'use_presets'
  | 'change_settings'
  | 'use_clipboard'
  | 'manage_elements'
  | 'use_alignment'
  | 'view_preview'
  | 'export_json'
  | 'import_templates'
  | 'use_grid'
  | 'use_layers_panel'
  | 'edit_properties'
  | 'view_changelog'
  | 'view_label_history'
  | 'use_zoom'
  | 'manage_categories'
  | 'use_field_undo'
  | 'duplicate_templates'
  | 'submit_templates'
  | 'view_dashboard'
  | 'use_keyboard_shortcuts'
  | 'view_help'
  | 'share_templates'
  | 'lock_templates'
  | 'version_templates'
  | 'use_snap_to_grid'
  | 'use_rulers'
  | 'use_multi_select'
  | 'resize_elements'
  | 'configure_print_settings'
  | 'bulk_print'
  | 'reprint_labels'
  | 'use_csv_import'
  | 'edit_field_data'
  | 'use_barcode_scanner'
  | 'use_mobile_scan'
  | 'view_shared_templates'
  | 'comment_templates'
  | 'view_own_activity'
  | 'view_print_history';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isApproved: boolean;
  mustChangePassword: boolean;
  username: string | null;
  permissions: PermissionType[];
  signInWithUsername: (username: string, password: string) => Promise<{ error: Error | null; needsApproval?: boolean; mustChangePassword?: boolean }>;
  signOut: () => Promise<void>;
  hasPermission: (permission: PermissionType) => boolean;
  refreshPermissions: () => Promise<void>;
  checkApproval: () => Promise<boolean>;
  changePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
  // Impersonation
  impersonatingUser: string | null;
  startImpersonating: (username: string, perms: PermissionType[]) => void;
  stopImpersonating: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<PermissionType[]>([]);
  const [impersonatingUser, setImpersonatingUser] = useState<string | null>(null);
  const [impersonatedPermissions, setImpersonatedPermissions] = useState<PermissionType[] | null>(null);

  const fetchUserProfile = useCallback(async (userId: string) => {
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('username, is_approved, must_change_password')
        .eq('user_id', userId)
        .maybeSingle();

      if (profileData) {
        setUsername(profileData.username);
        setIsApproved(profileData.is_approved);
        setMustChangePassword(profileData.must_change_password);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setProfileLoaded(true);
    }
  }, []);

  const fetchUserPermissions = useCallback(async (userId: string) => {
    try {
      // Check if user is admin
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();

      const userIsAdmin = !!roleData;
      setIsAdmin(userIsAdmin);

      if (userIsAdmin) {
        // Admins have all permissions
        setPermissions([
          'access_editor',
          'view_templates',
          'edit_templates',
          'delete_templates',
          'manage_own_templates',
          'use_kiosk',
          'view_data_panel',
          'use_json_editor',
          'batch_import',
          'print_labels',
          'export_pdf',
          'use_data_carousel',
          'change_label_size',
          'use_presets',
          'change_settings',
          'use_clipboard',
          'manage_elements',
          'use_alignment',
          'view_preview',
          'export_json',
          'import_templates',
          'use_grid',
          'use_layers_panel',
          'edit_properties',
          'view_changelog',
          'view_label_history',
          'use_zoom',
          'manage_categories',
          'use_field_undo',
          'duplicate_templates',
          'submit_templates',
          'view_dashboard',
          'use_keyboard_shortcuts',
          'view_help',
          'share_templates',
          'lock_templates',
          'version_templates',
          'use_snap_to_grid',
          'use_rulers',
          'use_multi_select',
          'resize_elements',
          'configure_print_settings',
          'bulk_print',
          'reprint_labels',
          'use_csv_import',
          'edit_field_data',
          'use_barcode_scanner',
          'use_mobile_scan',
          'view_shared_templates',
          'comment_templates',
          'view_own_activity',
          'view_print_history',
        ]);
      } else {
        // Fetch permissions from user's groups
        const { data: userGroups } = await supabase
          .from('user_groups')
          .select('group_id')
          .eq('user_id', userId);

        if (userGroups && userGroups.length > 0) {
          const groupIds = userGroups.map(ug => ug.group_id);
          const { data: groupPerms } = await supabase
            .from('group_permissions')
            .select('permission')
            .in('group_id', groupIds);

          if (groupPerms) {
            const uniquePerms = [...new Set(groupPerms.map(gp => gp.permission))] as PermissionType[];
            setPermissions(uniquePerms);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  }, []);

  const refreshPermissions = useCallback(async () => {
    if (user) {
      await fetchUserPermissions(user.id);
    }
  }, [user, fetchUserPermissions]);

  const checkApproval = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    
    const { data } = await supabase
      .from('profiles')
      .select('is_approved')
      .eq('user_id', user.id)
      .maybeSingle();

    const approved = data?.is_approved ?? false;
    setIsApproved(approved);
    return approved;
  }, [user]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Defer Supabase calls with setTimeout
        if (session?.user) {
          setTimeout(() => {
            fetchUserProfile(session.user.id);
            fetchUserPermissions(session.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
          setIsApproved(false);
          setUsername(null);
          setPermissions([]);
          setProfileLoaded(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        fetchUserProfile(session.user.id);
        fetchUserPermissions(session.user.id);
      } else {
        setProfileLoaded(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchUserProfile, fetchUserPermissions]);

  const signInWithUsername = async (username: string, password: string) => {
    // Convert username to fake email format for Supabase auth
    const fakeEmail = `${username.toLowerCase()}@labelmaker.local`;
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email: fakeEmail,
      password,
    });

    if (error) {
      // Log failed login attempt - use a fire-and-forget approach
      logActivityDirect('unknown', username, 'login', 'auth', `Failed login attempt for ${username}`, { reason: error.message }, false);
      return { error };
    }

    // Log successful login
    if (data.user) {
      const profileData2 = await supabase
        .from('profiles')
        .select('username')
        .eq('user_id', data.user.id)
        .maybeSingle();
      const resolvedUsername = profileData2?.data?.username || username;
      logActivityDirect(data.user.id, resolvedUsername, 'login', 'auth', `User ${resolvedUsername} logged in`);
    }

    // Check if user is approved and needs password change
    if (data.user) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('is_approved, must_change_password')
        .eq('user_id', data.user.id)
        .maybeSingle();

      if (!profileData?.is_approved) {
        return { error: null, needsApproval: true };
      }

      if (profileData?.must_change_password) {
        setMustChangePassword(true);
        return { error: null, mustChangePassword: true };
      }
    }

    return { error: null };
  };

  const changePassword = async (newPassword: string): Promise<{ error: Error | null }> => {
    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (updateError) {
      return { error: updateError };
    }

    // Update the profile to mark password as changed
    if (user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ must_change_password: false })
        .eq('user_id', user.id);

      if (profileError) {
        console.error('Error updating profile:', profileError);
        return { error: profileError };
      }

      setMustChangePassword(false);

      // Log password change
      logActivityDirect(user.id, username || 'unknown', 'password_changed', 'auth', `User ${username || 'unknown'} changed password`);

      // Send confirmation email (fire-and-forget)
      supabase.functions.invoke('notify-user', {
        body: {
          userId: user.id,
          templateName: 'password-changed',
          templateData: { changedAt: new Date().toUTCString() },
        },
      }).catch((e) => console.error('notify-user failed', e));
    }

    return { error: null };
  };

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchUserProfile(user.id);
    }
  }, [user, fetchUserProfile]);

  const signOut = async () => {
    // Log before signing out (we still have user context)
    if (user && username) {
      await logActivityDirect(user.id, username, 'logout', 'auth', `User ${username} logged out`);
    }
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsApproved(false);
    setMustChangePassword(false);
    setUsername(null);
    setPermissions([]);
    setProfileLoaded(false);
  };

  const hasPermission = useCallback((permission: PermissionType) => {
    // When impersonating, use impersonated permissions
    if (impersonatedPermissions !== null) {
      return impersonatedPermissions.includes(permission);
    }
    if (isAdmin) return true;
    return permissions.includes(permission);
  }, [isAdmin, permissions, impersonatedPermissions]);

  const startImpersonating = useCallback((username: string, perms: PermissionType[]) => {
    setImpersonatingUser(username);
    setImpersonatedPermissions(perms);
  }, []);

  const stopImpersonating = useCallback(() => {
    setImpersonatingUser(null);
    setImpersonatedPermissions(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading: loading || (!!user && !profileLoaded),
        isAdmin,
        isApproved,
        mustChangePassword,
        username,
        permissions,
        signInWithUsername,
        signOut,
        hasPermission,
        refreshPermissions,
        checkApproval,
        changePassword,
        refreshProfile,
        impersonatingUser,
        startImpersonating,
        stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
